/* eslint-disable complexity */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-console */
import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { putWithAuth } from "protocol-setup/redux/makeFetch";
import {
  FormTextInput,
  FormCheckboxGroup,
  FormTextarea,
  FormSelect,
  FormDatePicker
} from "protocol-setup/components/FormComponents";
import {
  FormButtonGroup,
  FormButtonGroupHeader
} from "protocol-setup/components/FormComponents/FormButtonGroup";
import { saveProtocolInfoPath } from "protocol-setup/redux/constants";
import { useHistory, useLocation } from "react-router";
import { useContactList } from "protocol-setup/redux/useLovs";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import {
  OpenSuccessNotification,
  OpenErrorNotification
} from "protocol-setup/components/notification";
import formSchema from "./infoFormSchema";
import {
  formatListOfChanges,
  requestTypeOptions,
  changeOptions,
  protocolTypeOptions,
  indTypeOptions,
  extractListOfChanges,
  extractContactsByType,
  tabNames
} from "./orHelpers";
import OEWGDateInput from "./OEWGDateInput";

const onSubmit = async (data, onSuccessCallback, event) => {
  event.preventDefault();
  const loc = formatListOfChanges(data.listOfChanges);
  const emailArr = data.otherEmailContacts
    ? data.otherEmailContacts.split(",").map((email) => {
      return email.trim();
    })
    : [];
  const formattedData = {
    ...data,
    otherEmailContacts: emailArr,
    protSetupContacts: [],
    requestStatus: "DRAFT",
    formPartType: "OPEN_RAVE"
  };

  if (formattedData?.raveContact) {
    formattedData.raveContact.forEach((key) => {
      formattedData.protSetupContacts.push({ perId: key, contactType: "RAVE" });
    });
  }
  if (formattedData.openContact) {
    formattedData.openContact.forEach((key) => {
      formattedData.protSetupContacts.push({ perId: key, contactType: "OPEN" });
    });
  }

  Object.keys(loc).forEach((key) => {
    formattedData[key] = loc[key];
  });

  if (!loc.otherChanges) {
    formattedData.describeOtherChanges = null;
  }

  delete formattedData.listOfChanges;
  delete formattedData.raveContact;
  delete formattedData.openContact;
  delete formattedData.revisionEligibilityCriteria;

  try {
    const { requestNumber } = await putWithAuth(
      saveProtocolInfoPath,
      formattedData
    );
    if (requestNumber) {
      onSuccessCallback({ ...data, requestNumber });
    } else {
      OpenErrorNotification({
        message:
          "Unable to complete the request. Contact CTSU Help Desk, ctsucontact@westat.com.",
        autoHide: true
      });
    }
  } catch (error) {
    OpenErrorNotification({
      message:
        "Unable to complete the request. Contact CTSU Help Desk, ctsucontact@westat.com.",
      autoHide: true
    });
  }
};

export const InfoFormCore = ({
  defaultValues,
  today,
  contactList,
  requestNumber,
  selectedRequest,
  setDirtyTab,
  isFormReadOnly,
  fetchPsData,
  isSubmitFormEnabled
}) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { isDirty, dirtyFields }
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues,
    mode: "onChange"
  });

  const [showIndFields, setShowIndFields] = useState(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setDirtyTab(isDirty);
  }, [isDirty]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (location.state?.showSuccessMessage) {
      OpenSuccessNotification({
        message: location.state.message || "Record successfully created",
        autoHide: true
      });

      // Clear the state to prevent showing the message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const onSuccess = (data) => {
    if (selectedRequest?.requestNumber) {
      fetchPsData();
      // Show notification for updates since we're not navigating away
      OpenSuccessNotification({
        message: "Record successfully updated",
        autoHide: true
      });
    } else {
      history.push(`/protocol-setup/open-rave/${data.requestNumber}`, {
        showSuccessMessage: true,
        message: "Record successfully created"
      });
    }
    reset(getValues());
  };
  const resetForm = () => {
    reset(defaultValues);
    setShowIndFields(defaultValues.indType === "Non-CTEP held IND");
  };
  // Wrap onSubmit with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback, event) =>
      onSubmit(data, onSuccessCallback, event),
    selectedRequest.requestStatus === "DRAFT"
      ? dirtyFields
      : { initialSave: true },
    tabNames.PROTOCOL_INFORMATION,
    selectedRequest.requestStatus === "DRAFT" ? "Updates" : "Initial Save"
  );

  return (
    <div
      className={`form-layout-wrapper mt-3 ${isFormReadOnly ? "read-only" : ""
        }`}
    >
      <form
        onSubmit={handleSubmit((data, event) =>
          onSubmitWithHistory(data, onSuccess, event)
        )}
      >
        <div className="form-layout-header">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroupHeader
              control={control}
              reset={reset}
              formTitle="Protocol Information"
            />
          )}
          <div className="form-layout-title invisible">
            Protocol Setup, Protocol Information
          </div>
        </div>
        <div
          className={`form-layout-body p-0 ${isFormReadOnly ? "pointer-events-none" : ""
            }`}
        >
          <div className="invisible"></div>
          <ul>
            <li className="row form-sub-group-1 px-3">
              <div className="col-4 mr-4 ">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="requestDate"
                  control={control}
                  type="text"
                  readOnly
                  required
                />
              </div>
              <div className="col-md-4 mr-4"></div>
              <div className="col-md-4"></div>
            </li>

            <li className="row form-sub-group-2 px-3">
              <div className="col-md-4 mr-4">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="lpo"
                  control={control}
                  required
                  readOnly
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="protocolNumber"
                  control={control}
                  required
                  readOnly
                />
              </div>
              <div className="col-md-4">
                <FormTextarea
                  section="openRave"
                  form="infoForm"
                  name="protocolTitle"
                  control={control}
                  required
                  readOnly
                />
              </div>
            </li>
            <li className="row form-sub-group ">
              <div className="col-md-4 mr-4">
                <FormSelect
                  section="openRave"
                  form="infoForm"
                  name="requestType"
                  control={control}
                  options={requestTypeOptions}
                  required
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormCheckboxGroup
                  section="openRave"
                  form="infoForm"
                  name="listOfChanges"
                  control={control}
                  options={changeOptions}
                  required
                  readOnly={isFormReadOnly}
                  wrapperClassName={isFormReadOnly ? 'read-only-show' : ''}
                  disabled={isFormReadOnly}
                />
                {watch("listOfChanges")?.includes("otherChanges") && (
                  <FormTextarea
                    section="openRave"
                    form="infoForm"
                    name="describeOtherChanges"
                    control={control}
                    rows={3}
                    required
                    readOnly={isFormReadOnly}
                  />
                )}
              </div>
              <div className="col-md-4">
                <FormTextarea
                  section="openRave"
                  form="infoForm"
                  name="revisionEligibilityCriteria"
                  control={control}
                  rows={3}
                  trigger={trigger}
                  readOnly={isFormReadOnly}
                />
              </div>
            </li>
            <li className="row form-sub-group-4 px-3">
              <div className="col-md-4 mr-4">
                <FormSelect
                  section="openRave"
                  form="infoForm"
                  name="protocolType"
                  control={control}
                  options={protocolTypeOptions}
                  required
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormSelect
                  section="openRave"
                  form="infoForm"
                  name="indType"
                  control={control}
                  options={indTypeOptions}
                  required
                  readOnly={isFormReadOnly}
                  onChangeCallback={(value) => {
                    setShowIndFields(value === "Non-CTEP held IND");
                    setValue("indAgent", "");
                    setValue("indHolder", "");
                    setValue("indNumber", "");
                  }}
                />
              </div>
              <div className="col-md-4"></div>
            </li>
            <li
              className={`row form-sub-group ${showIndFields ? "" : "d-none"}`}
            >
              <div className="col-md-4 mr-4">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="indAgent"
                  control={control}
                  required
                  trigger={trigger}
                  disabled={!showIndFields}
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="indHolder"
                  control={control}
                  required
                  trigger={trigger}
                  disabled={!showIndFields}
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4">
                <FormTextInput
                  section="openRave"
                  form="infoForm"
                  name="indNumber"
                  control={control}
                  required
                  trigger={trigger}
                  disabled={!showIndFields}
                  readOnly={isFormReadOnly}
                />
              </div>
            </li>
            <li className="row form-sub-group-5 px-3">
              <div className="col-md-4 mr-4">
                <FormDatePicker
                  section="openRave"
                  form="infoForm"
                  name="tentativeActivationDate"
                  control={control}
                  required
                  type="text"
                  minDate={today}
                  readOnly={isFormReadOnly}
                />
              </div>
              <div className="col-md-4 mr-4">
                <OEWGDateInput
                  name="oewgDate"
                  control={control}
                  protocolNumber={selectedRequest?.protocolNumber}
                  requestStatus={selectedRequest?.requestStatus}
                  setValue={setValue}
                />
              </div>
              <div className="col-md-4"></div>
            </li>
            <li className="row form-sub-group">
              <div className="col-md-4 mr-4">
                <FormSelect
                  section="openRave"
                  form="infoForm"
                  name="openContact"
                  control={control}
                  isMulti
                  readOnly={isFormReadOnly}
                  options={contactList?.map((item) => ({
                    value: item.perId,
                    label: `${item.lastName}, ${item.firstName} (${item.ctepPersonId}) `
                  }))}
                  required
                />
              </div>
              <div className="col-md-4 mr-4">
                <FormSelect
                  section="openRave"
                  form="infoForm"
                  name="raveContact"
                  control={control}
                  isMulti
                  readOnly={isFormReadOnly}
                  options={contactList?.map((item) => ({
                    value: item.perId,
                    label: `${item.lastName}, ${item.firstName} (${item.ctepPersonId}) `
                  }))}
                  required
                />
              </div>
              <div className="col-md-4">
                <FormTextarea
                  section="openRave"
                  form="infoForm"
                  name="otherEmailContacts"
                  control={control}
                  type="email"
                  trigger={trigger}
                  readOnly={isFormReadOnly}
                />
              </div>
            </li>
          </ul>
        </div>
        <div className="form-layout-footer pt-3 pb-0">
          {isFormReadOnly ? (
            <></>
          ) : (
            <FormButtonGroup
              control={control}
              formTitle="Protocol Information"
              reset={resetForm}
              requestNumber={requestNumber}
              formPartType={selectedRequest?.formPartType}
              handleSubmit={handleSubmit}
              isSubmitFormEnabled={isSubmitFormEnabled}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </form>
    </div>
  );
};

function InfoForm({
  selectedRequest,
  requestNumber,
  setDirtyTab,
  isFormReadOnly,
  fetchPsData,
  isSubmitFormEnabled
}) {
  const today = format(new Date(), "dd-MMM-yyyy");

  const {
    contactList: { data = [] }
  } = useContactList(selectedRequest?.lpo);

  const getEmailContacts = (otherEmailContacts) => {
    if (otherEmailContacts) {
      return Array.isArray(otherEmailContacts)
        ? otherEmailContacts.join(",")
        : otherEmailContacts;
    }
    return "";
  };

  const defaultValues = useMemo(
    () => ({
      requestNumber: selectedRequest?.requestNumber || "",
      requestDate: selectedRequest?.requestDate || today,
      lpo: selectedRequest?.lpo || "",
      protocolNumber:
        selectedRequest?.protocol || selectedRequest?.protocolNumber || "",
      protocolTitle: selectedRequest?.protocolTitle,
      requestType: selectedRequest?.requestType || "",
      listOfChanges:
        selectedRequest?.listOfChanges ||
        extractListOfChanges(selectedRequest) ||
        [],
      describeOtherChanges: selectedRequest?.describeOtherChanges || "",
      revisionEligibilityCriteria:
        selectedRequest?.revisionEligibilityCriteria || "",
      protocolType: selectedRequest?.protocolType || "",
      indType: selectedRequest?.indType || "",
      indAgent: selectedRequest?.indAgent || "",
      indHolder: selectedRequest?.indHolder || "",
      indNumber: selectedRequest?.indNumber || "",
      tentativeActivationDate: selectedRequest?.tentativeActivationDate || "",
      oewgDate: selectedRequest?.oewgDate,
      raveContact:
        selectedRequest?.raveContact ||
        extractContactsByType(selectedRequest, "RAVE") ||
        [],
      openContact:
        selectedRequest?.openContact ||
        extractContactsByType(selectedRequest, "OPEN") ||
        [],
      otherEmailContacts: getEmailContacts(selectedRequest?.otherEmailContacts)
    }),
    [selectedRequest, today]
  );

  return (
    <InfoFormCore
      defaultValues={defaultValues}
      today={today}
      contactList={data}
      requestNumber={requestNumber}
      selectedRequest={selectedRequest}
      setDirtyTab={setDirtyTab}
      isFormReadOnly={isFormReadOnly}
      fetchPsData={fetchPsData}
      isSubmitFormEnabled={isSubmitFormEnabled}
    />
  );
}

export default InfoForm;
